import { BrowserRouter, HashRouter, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/query';
import { AuthProvider } from './lib/auth';
import { BrandProvider } from './lib/brand';
import { Shell } from './components/Shell';
import { BrandRoute } from './components/BrandRoute';
import { PageHeader } from './components/Card';
import { EmptyState } from './components/EmptyState';
import { APP_ROUTES } from './routes';
import { AuthGate } from './pages/auth/AuthGate';
import Login from './pages/auth/Login';
import AuthCallback from './pages/auth/AuthCallback';

export const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true } as const;

function NotFound() {
  return (
    <>
      <PageHeader title="Page not found" />
      <section className="card">
        <EmptyState title="This page does not exist" action={{ label: 'Go to all brands', to: '/' }}>
          The link may be old. Every screen is in the sidebar.
        </EmptyState>
      </section>
    </>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        element={
          <AuthGate>
            <BrandProvider>
              <Shell />
            </BrandProvider>
          </AuthGate>
        }
      >
        {APP_ROUTES.map(({ page, Component }) => (
          <Route
            key={page.key}
            // Screens that are not exact-match own everything under their path, so a page can
            // add sub-views (e.g. /b/:brand/opportunities/:id) with nested <Routes> of its own.
            path={page.end ? page.path : `${page.path}/*`}
            element={
              page.brand ? (
                <BrandRoute title={page.title}>
                  <Component />
                </BrandRoute>
              ) : (
                <Component />
              )
            }
          />
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

// The click-around demo build (vite.demo.config.ts) runs inside a hosted page, so it routes by hash.
const Router = import.meta.env.VITE_DEMO ? HashRouter : BrowserRouter;

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router future={ROUTER_FUTURE}>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}
